import { type ComponentProps, type RefObject, useEffect, useRef, useState } from 'react';
import { Root, List, Trigger, Content } from '@radix-ui/react-tabs';

import { cn } from '@/util/utils';

/**
 * ui-tabs, READ 8:728: a muted track padded 3px and rounded-lg; triggers in the condensed
 * label style, muted until active, when they lift onto the background with shadow-sm.
 */
function Tabs({ className, ref, ...props }: ComponentProps<typeof Root>) {
    return (
        <Root
            data-slot="tabs"
            className={cn('flex flex-col gap-2', className)}
            ref={ref}
            {...props}
        />
    );
}

type TabsListProps = ComponentProps<typeof List> & {
    size?: 'default' | 'sm';
    /**
     * `underline`, Order detail's strip (2694:106753): no track, the tabs 24 apart with a 2px
     * rule under the chosen one, which takes the primary colour with its label.
     */
    variant?: 'default' | 'underline';
    /**
     * A strip that may not fit a phone: it scrolls sideways rather than wrapping, fades the edge
     * that has more tabs past it, and keeps the chosen tab in view (ALS-263). Without the fade a
     * strip cut at a tab boundary reads as complete, and the last destination is never found.
     */
    scrollable?: boolean;
};

type Overflow = 'start' | 'end' | 'both' | null;

/** The width of a scrollable strip's edge fade, in CSS pixels, as its mask draws it. */
const FADE = 32;

function TabsList({
    className,
    size = 'default',
    variant = 'default',
    scrollable = false,
    ref,
    ...props
}: TabsListProps) {
    const own = useRef<HTMLDivElement>(null);
    const overflow = useScrollCue(own, scrollable);
    return (
        <List
            data-slot="tabs-list"
            data-size={size}
            data-variant={variant}
            data-overflow={overflow ?? undefined}
            data-scrollable={scrollable || undefined}
            className={cn(
                'group/tabs bg-muted inline-flex min-w-0 items-center rounded-lg p-[3px]',
                // Padded 8 above and 18 at the sides, as the frame draws the strip.
                'data-[variant=underline]:flex data-[variant=underline]:gap-6 data-[variant=underline]:rounded-none data-[variant=underline]:bg-transparent data-[variant=underline]:px-[18px] data-[variant=underline]:pt-2 data-[variant=underline]:pb-0',
                // The fade is a mask, so it only takes alpha: the strip's own colours run under it.
                scrollable && [
                    'max-w-full overflow-x-auto',
                    'data-[overflow=end]:[mask-image:linear-gradient(to_right,black_calc(100%_-_32px),transparent)]',
                    'data-[overflow=start]:[mask-image:linear-gradient(to_left,black_calc(100%_-_32px),transparent)]',
                    'data-[overflow=both]:[mask-image:linear-gradient(to_right,transparent,black_32px,black_calc(100%_-_32px),transparent)]',
                ],
                className,
            )}
            ref={(node) => {
                own.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
            }}
            {...props}
        />
    );
}

/**
 * Which ends of a scrollable strip hide tabs, kept current as it scrolls and resizes, and the
 * chosen tab scrolled into view whenever the choice changes. The strip is scrolled rather than
 * the tab asked to scrollIntoView, which would move the page too.
 */
function useScrollCue(list: RefObject<HTMLDivElement | null>, enabled: boolean): Overflow {
    const [overflow, setOverflow] = useState<Overflow>(null);
    useEffect(() => {
        const strip = list.current;
        if (!enabled || !strip) return;
        const measure = () => {
            const max = strip.scrollWidth - strip.clientWidth;
            const start = max > 1 && strip.scrollLeft > 1;
            const end = max > 1 && strip.scrollLeft < max - 1;
            setOverflow(start && end ? 'both' : start ? 'start' : end ? 'end' : null);
        };
        const reveal = () => {
            const active = strip.querySelector<HTMLElement>('[role=tab][data-state=active]');
            if (!active) return;
            const bounds = strip.getBoundingClientRect();
            const tab = active.getBoundingClientRect();
            // Clear of the 32px fade, so the chosen tab is never the one half faded out. An edge
            // with more tabs past it is faded, so a tab under its fade counts as out of view: a
            // tapped tab the browser has just scrolled into sight sits there (ALS-87).
            const max = strip.scrollWidth - strip.clientWidth;
            const fadeStart = strip.scrollLeft > 1 ? FADE : 0;
            const fadeEnd = strip.scrollLeft < max - 1 ? FADE : 0;
            const by =
                tab.left < bounds.left + fadeStart
                    ? tab.left - bounds.left - FADE
                    : tab.right > bounds.right - fadeEnd
                      ? tab.right - bounds.right + FADE
                      : 0;
            if (by !== 0) strip.scrollBy({ left: by });
            measure();
        };
        reveal();
        strip.addEventListener('scroll', measure, { passive: true });
        // Choosing a tab moves focus to it after its state changes, and the browser's own scroll
        // to the focused tab can put it back under the fade; reveal again once focus has landed.
        strip.addEventListener('focusin', reveal);
        const resized = new ResizeObserver(measure);
        resized.observe(strip);
        const chosen = new MutationObserver(reveal);
        chosen.observe(strip, { subtree: true, attributeFilter: ['data-state'] });
        return () => {
            strip.removeEventListener('scroll', measure);
            strip.removeEventListener('focusin', reveal);
            resized.disconnect();
            chosen.disconnect();
        };
    }, [list, enabled]);
    return enabled ? overflow : null;
}

type TabsTriggerProps = ComponentProps<typeof Trigger> & {
    /** The count pill the notifications tabs carry. Zero hides it rather than drawing an empty circle. */
    count?: number;
};

function TabsTrigger({ className, count, children, ref, ...props }: TabsTriggerProps) {
    return (
        <Trigger
            data-slot="tabs-trigger"
            ref={ref}
            className={cn(
                'text-label-md text-muted-foreground inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-md border border-transparent px-2.5 py-1.5 whitespace-nowrap',
                'group-data-[size=sm]/tabs:px-2 group-data-[size=sm]/tabs:py-1',
                // A finger gets the project's 44px target; the underline strip is taller already.
                'pointer-coarse:min-h-11',
                // A scrollable strip's tab never shrinks below its label: squeezed, the labels ran
                // into each other and the strip never overflowed, so it never scrolled (ALS-87).
                'group-data-[scrollable=true]/tabs:min-w-max',
                'focus-visible:outline-ring transition-[color,box-shadow] duration-(--duration-fast) outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
                'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
                // Underline: a 16px glyph and its label 7 apart, 16 above and below, on a 2px rule
                // that only the chosen tab shows.
                'group-data-[variant=underline]/tabs:flex-none group-data-[variant=underline]/tabs:gap-[7px] group-data-[variant=underline]/tabs:rounded-none group-data-[variant=underline]/tabs:border-0 group-data-[variant=underline]/tabs:border-b-2 group-data-[variant=underline]/tabs:border-transparent group-data-[variant=underline]/tabs:px-0 group-data-[variant=underline]/tabs:py-4',
                'group-data-[variant=underline]/tabs:data-[state=active]:border-primary group-data-[variant=underline]/tabs:data-[state=active]:text-primary group-data-[variant=underline]/tabs:data-[state=active]:bg-transparent group-data-[variant=underline]/tabs:data-[state=active]:shadow-none',
                'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
                className,
            )}
            {...props}
        >
            {children}
            {count !== undefined ? (
                <span
                    data-slot="tabs-count"
                    hidden={count === 0}
                    className="bg-primary text-primary-foreground inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 font-sans text-xs font-semibold tracking-normal normal-case"
                >
                    {count}
                </span>
            ) : null}
        </Trigger>
    );
}

function TabsContent({ className, ref, ...props }: ComponentProps<typeof Content>) {
    return (
        <Content
            data-slot="tabs-content"
            ref={ref}
            className={cn(
                'focus-visible:outline-ring flex-1 rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
                className,
            )}
            {...props}
        />
    );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
